import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityWarningContent } from '@internal/backstage-plugin-devops-react';
import {
  RuleEntity,
  RuleSetEntity,
  RULE_SEVERITIES,
} from '@internal/backstage-plugin-catalog-devops-common';
import { useRulesByRuleSet } from '@internal/backstage-plugin-devops-react';
import {
  affectedCount,
  maxSavingOf,
  pillClass,
  ruleTitle,
  severityOf,
  severityRank,
  siblingTabPath,
} from './shared';
import styles from './entity-page.module.css';

const RING_CIRCUMFERENCE = 257.6;

/**
 * RuleSet page Overview, 1:1 with design/ruleset-page-mockup.html: score
 * ring computed CLIENT-SIDE from the member rules with the same formula as
 * the account score (passed ÷ (passed + failed), Unknown excluded — the
 * rulesStatisticsApi radial is retired), status / failed-by-severity /
 * blast-radius mini cards and the worst-rules preview.
 */
export function RuleSetOverview() {
  const { entity } = useEntity();
  const ruleSet = entity as RuleSetEntity;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { rules } = useRulesByRuleSet(ruleSet.metadata.name);
  const ruleEntities = useMemo(() => rules as RuleEntity[], [rules]);

  const stats = useMemo(() => {
    const failed = ruleEntities.filter(rule => rule.spec.result === 'Failed');
    const passed = ruleEntities.filter(rule => rule.spec.result === 'Passed');
    const unknown = ruleEntities.length - failed.length - passed.length;
    const scored = passed.length + failed.length;
    const score = scored ? Math.round((passed.length / scored) * 100) : 0;
    const failedIds = failed.flatMap(rule =>
      (rule.spec.resources ?? []).map(resource => resource.id),
    );
    const affected = new Set(failedIds);
    const regions = new Set(
      failedIds.map(id => id.split('_')[1]).filter(Boolean),
    );
    const saving = failed.reduce(
      (sum, rule) => sum + (maxSavingOf(rule) ?? 0),
      0,
    );
    return {
      failed,
      passed,
      unknown,
      score,
      affected: affected.size,
      regions: regions.size,
      saving,
    };
  }, [ruleEntities]);

  const failedBySeverity = useMemo(
    () =>
      (RULE_SEVERITIES as readonly string[])
        .slice(0, 4)
        .map(severity => ({
          severity,
          count: stats.failed.filter(rule => severityOf(rule) === severity)
            .length,
        })),
    [stats.failed],
  );

  const worstRules = useMemo(
    () =>
      [...stats.failed]
        .sort(
          (a, b) =>
            severityRank(severityOf(a)) - severityRank(severityOf(b)) ||
            affectedCount(b) - affectedCount(a),
        )
        .slice(0, 5),
    [stats.failed],
  );

  const ringClass = () => {
    if (stats.score >= 80) {
      return styles.ringSuccess;
    }
    if (stats.score >= 50) {
      return styles.ringWarning;
    }
    return styles.ringDanger;
  };

  const barColor = (severity: string): string => {
    if (severity === 'Critical') {
      return styles.barDanger;
    }
    if (severity === 'High') {
      return styles.barWarning;
    }
    return styles.barInfo;
  };

  return (
    <>
      <EntityWarningContent />

      <div className={styles.summary}>
        <div className={`${styles.card} ${styles.scoreCard}`}>
          <div className={styles.ring}>
            <svg width="96" height="96" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                stroke="var(--bui-bg-neutral-2)"
                strokeWidth="9"
              />
              <circle
                cx="48"
                cy="48"
                r="41"
                fill="none"
                className={ringClass()}
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={
                  RING_CIRCUMFERENCE * (1 - stats.score / 100)
                }
              />
            </svg>
            <div className={styles.ringVal}>
              <div>
                <b>{stats.score}</b>
                <span>score</span>
              </div>
            </div>
          </div>
          <div className={styles.scoreMeta}>
            <h3>Framework score</h3>
            <p>
              Passed ÷ (passed + failed) of this framework’s rules;{' '}
              <b>Unknown</b> rules are excluded — same formula as the account
              score.
            </p>
          </div>
        </div>

        <div className={styles.miniCards}>
          <div className={`${styles.card} ${styles.miniCard}`}>
            <h4 className={styles.miniTitle}>Rules by status</h4>
            <div className={styles.statLine}>
              <span className={`${styles.pill} ${styles.pillFailed}`}>
                <span className={styles.pillDot} />
                Failed
              </span>
              <b>{stats.failed.length}</b>
              <span className={styles.bar}>
                <i
                  className={`${styles.barFill} ${styles.barDanger}`}
                  style={{
                    width: `${ruleEntities.length ? (stats.failed.length / ruleEntities.length) * 100 : 0}%`,
                  }}
                />
              </span>
            </div>
            <div className={styles.statLine}>
              <span className={`${styles.pill} ${styles.pillPassed}`}>
                <span className={styles.pillDot} />
                Passed
              </span>
              <b>{stats.passed.length}</b>
              <span className={styles.bar}>
                <i
                  className={`${styles.barFill} ${styles.barSuccess}`}
                  style={{
                    width: `${ruleEntities.length ? (stats.passed.length / ruleEntities.length) * 100 : 0}%`,
                  }}
                />
              </span>
            </div>
            <div className={styles.statLine}>
              <span className={`${styles.pill} ${styles.pillUnknown}`}>
                Unknown
              </span>
              <b>{stats.unknown}</b>
              <span className={styles.bar}>
                <i
                  className={`${styles.barFill} ${styles.barMuted}`}
                  style={{
                    width: `${ruleEntities.length ? (stats.unknown / ruleEntities.length) * 100 : 0}%`,
                  }}
                />
              </span>
            </div>
          </div>

          <div className={`${styles.card} ${styles.miniCard}`}>
            <h4 className={styles.miniTitle}>Failed by severity</h4>
            {failedBySeverity.map(entry => (
              <div key={entry.severity} className={styles.statLine}>
                <span
                  className={`${styles.pill} ${pillClass(styles, entry.severity)}`}
                >
                  <span className={styles.pillDot} />
                  {entry.severity}
                </span>
                <b>{entry.count}</b>
                <span className={styles.bar}>
                  <i
                    className={`${styles.barFill} ${barColor(entry.severity)}`}
                    style={{
                      width: `${(entry.count / Math.max(1, ...failedBySeverity.map(item => item.count))) * 100}%`,
                    }}
                  />
                </span>
              </div>
            ))}
          </div>

          <div className={`${styles.card} ${styles.miniCard}`}>
            <h4 className={styles.miniTitle}>Blast radius</h4>
            <div className={styles.statLine}>
              <span className={styles.muted}>Resources</span>
              <b style={{ marginLeft: 'auto' }}>{stats.affected}</b>
            </div>
            <div className={styles.statLine}>
              <span className={styles.muted}>Regions</span>
              <b style={{ marginLeft: 'auto' }}>{stats.regions}</b>
            </div>
            <div className={styles.statLine}>
              <span className={styles.muted}>Est. saving</span>
              <b
                style={{ marginLeft: 'auto' }}
                className={stats.saving ? styles.statMoney : styles.muted}
              >
                {stats.saving
                  ? `$${Math.round(stats.saving).toLocaleString('en-US')}/mo`
                  : '—'}
              </b>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.secTitle}>Worst rules in this framework</div>
      <div className={styles.card}>
        <table className={styles.table} aria-label="Worst rules preview">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Affected</th>
              <th>Est. saving</th>
            </tr>
          </thead>
          <tbody>
            {worstRules.map(rule => {
              const saving = maxSavingOf(rule);
              return (
                <tr
                  key={rule.metadata.name}
                  className={styles.row}
                  onClick={() =>
                    navigate(`/catalog/default/rule/${rule.metadata.name}`)
                  }
                >
                  <td>
                    <span className={styles.ruleName}>{ruleTitle(rule)}</span>{' '}
                    <span className={styles.ruleCode}>{rule.spec.type}</span>
                  </td>
                  <td>
                    <span
                      className={`${styles.pill} ${pillClass(styles, severityOf(rule))}`}
                    >
                      <span className={styles.pillDot} />
                      {severityOf(rule)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`${styles.pill} ${pillClass(styles, rule.spec.result ?? 'Unknown')}`}
                    >
                      <span className={styles.pillDot} />
                      {rule.spec.result}
                    </span>
                  </td>
                  <td className={styles.num}>{affectedCount(rule)}</td>
                  <td>
                    {saving ? (
                      <span className={styles.saving}>${saving}/mo</span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {worstRules.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  Nothing failed in this framework.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className={styles.tableFoot}>
          <Link to={siblingTabPath(pathname, 'rules')}>
            <b style={{ fontSize: 'var(--bui-font-size-2)' }}>
              View all {ruleEntities.length} rules →
            </b>
          </Link>
        </div>
      </div>
    </>
  );
}
